/**
 * Payment Registry
 *
 * Central registry for payment method plugins.
 * Payment methods are registered and can be retrieved by type.
 */

import type { MarketContext } from '../markets/types';
import type { PaymentRecord } from './split/types';
import type {
  PaymentAvailability,
  PaymentMethod,
  PaymentMethodConfig,
  SplitPaymentConfig,
} from './types';

/**
 * Extended split config for validation (combines backend config with frontend state)
 */
interface SplitValidationContext extends SplitPaymentConfig {
  payments: PaymentRecord[];
  remainingAmount: number;
}

/**
 * Payment Registry Class
 */
class PaymentRegistry {
  private methods: Map<string, PaymentMethod> = new Map();

  /**
   * Register a payment method
   */
  register(method: PaymentMethod): void {
    this.methods.set(method.type, method);
  }

  /**
   * Register multiple payment methods
   */
  registerMany(methods: PaymentMethod[]): void {
    for (const method of methods) {
      this.register(method);
    }
  }

  /**
   * Get a payment method by type
   */
  get(type: string): PaymentMethod | undefined {
    return this.methods.get(type);
  }

  /**
   * Check if a payment method is registered
   */
  has(type: string): boolean {
    return this.methods.has(type);
  }

  /**
   * Get all registered payment methods
   */
  getAll(): PaymentMethod[] {
    return Array.from(this.methods.values());
  }

  /**
   * Get registered method types
   */
  getTypes(): string[] {
    return Array.from(this.methods.keys());
  }

  /**
   * Get available payment methods for a context
   */
  getAvailable(context: {
    marketContext: MarketContext;
    amount: number;
    isSplitPayment: boolean;
    existingPayments: string[];
    backendConfigs?: PaymentMethodConfig[];
  }): Array<{ method: PaymentMethod; availability: PaymentAvailability }> {
    const { marketContext, amount, isSplitPayment, existingPayments, backendConfigs } = context;
    const available: Array<{ method: PaymentMethod; availability: PaymentAvailability }> = [];

    for (const method of this.methods.values()) {
      // Check if backend config allows this method
      const backendConfig = backendConfigs?.find((c) => c.type === method.type);
      if (backendConfig && !backendConfig.available) {
        continue;
      }

      // Check method's own availability logic
      const availability = method.checkAvailability({
        marketContext,
        amount,
        isSplitPayment,
        existingPayments,
      });

      // Apply backend config limits
      if (backendConfig) {
        if (backendConfig.limitMin != null) {
          availability.minAmount = Math.max(availability.minAmount ?? 0, backendConfig.limitMin);
        }
        if (backendConfig.limitMax != null) {
          availability.maxAmount = Math.min(
            availability.maxAmount ?? Infinity,
            backendConfig.limitMax
          );
        }
        if (!backendConfig.splitPayment) {
          availability.splitPaymentAllowed = false;
        }
      }

      // Final availability check
      if (availability.available) {
        // Check amount limits
        if (availability.minAmount != null && amount < availability.minAmount) {
          availability.available = false;
          availability.reason = 'amount_below_minimum';
        } else if (availability.maxAmount != null && amount > availability.maxAmount) {
          availability.available = false;
          availability.reason = 'amount_above_maximum';
        }

        // Check split payment allowance
        if (isSplitPayment && !availability.splitPaymentAllowed) {
          availability.available = false;
          availability.reason = 'split_payment_not_allowed';
        }

        // Check once-per-order
        if (availability.oncePerOrder && existingPayments.includes(method.type)) {
          availability.available = false;
          availability.reason = 'already_used';
        }

        // Check incompatibilities
        if (availability.incompatibleWith?.some((t) => existingPayments.includes(t))) {
          availability.available = false;
          availability.reason = 'incompatible_method_used';
        }
      }

      if (availability.available) {
        available.push({ method, availability });
      }
    }

    return available;
  }

  /**
   * Validate split payment configuration
   */
  validateSplitPayment(
    config: SplitValidationContext,
    newMethod: string,
    newAmount: number
  ): { valid: boolean; error?: string } {
    // Check max payments
    if (config.payments.length >= config.maxPayments) {
      return { valid: false, error: 'max_payments_reached' };
    }

    // Check minimum amount
    if (newAmount < config.minAmount) {
      return { valid: false, error: 'amount_below_minimum' };
    }

    // Check remaining amount
    if (newAmount > config.remainingAmount) {
      return { valid: false, error: 'amount_exceeds_remaining' };
    }

    // Check method-specific rules
    const method = this.get(newMethod);
    if (!method) {
      return { valid: false, error: 'method_not_found' };
    }

    // Check if method is already used (for once-per-order methods)
    const existingMethods = config.payments.map((p) => p.type);
    if (existingMethods.includes(newMethod)) {
      const availability = method.checkAvailability({
        marketContext: {} as MarketContext, // Simplified for validation
        amount: newAmount,
        isSplitPayment: true,
        existingPayments: existingMethods,
      });

      if (availability.oncePerOrder) {
        return { valid: false, error: 'method_once_per_order' };
      }
    }

    return { valid: true };
  }

  /**
   * Clear all registered methods (for testing)
   */
  clear(): void {
    this.methods.clear();
  }
}

/**
 * Global payment registry instance
 */
export const paymentRegistry = new PaymentRegistry();

/**
 * Get payment method (convenience function)
 */
export function getPaymentMethod(type: string): PaymentMethod | undefined {
  return paymentRegistry.get(type);
}

/**
 * Get available payment methods (convenience function)
 */
export function getAvailablePaymentMethods(context: {
  marketContext: MarketContext;
  amount: number;
  isSplitPayment: boolean;
  existingPayments: string[];
  backendConfigs?: PaymentMethodConfig[];
}): Array<{ method: PaymentMethod; availability: PaymentAvailability }> {
  return paymentRegistry.getAvailable(context);
}
